import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BaseComponent } from '@ids-components';
import { FirmwarePushFormComponent } from '../../shared-firmwares/firmware-push-form/firmware-push-form.component';
import { FirmwareUploadFormComponent } from '../../shared-firmwares/firmware-upload-form/firmware-upload-form.component';
import { FirmwareService } from '@ids-services';
import { ActionMenuItem } from '@microsec/models';

import { finalize } from 'rxjs';

import { AssignFirmwareFormComponent } from './assign-firmware-form/assign-firmware-form.component';
import { DELETE_LABEL } from '@microsec/constants';
import { ButtonModule } from 'primeng/button';
import { CardComponent, CommonTableComponent, LoadingOverlayComponent } from '@microsec/components';
import { TooltipModule } from 'primeng/tooltip';
import { DividerModule } from 'primeng/divider';
import { CommonModule } from '@angular/common';
import { ConstantPipe } from '@ids-pipes';

@Component({
  selector: 'app-device-firmwares',
  templateUrl: './device-firmwares.component.html',
  styleUrls: ['./device-firmwares.component.scss'],
  imports: [CommonModule, TooltipModule, ButtonModule, DividerModule, CardComponent, CommonTableComponent, LoadingOverlayComponent, ConstantPipe],
  providers: [ConstantPipe],
})
export class DeviceFirmwaresComponent extends BaseComponent implements OnInit, OnDestroy {
  isLoading = false;

  _device = null;

  get device() {
    return this._device;
  }

  @Input() set device(value: any) {
    this._device = value;
    if (!!this.device?.id) {
      this.getDeviceFirmware();
    }
  }

  firmwares: any[] = [];

  supportedDevices: any[] = [];

  deviceFirmware: any = null;

  cols: any[] = [
    { field: 'id', header: '#', width: 8 },
    { field: 'name', header: 'Firmware Name', width: 12 },
    { field: 'version', header: 'Firmware Version', width: 12 },
    { field: 'target_device', header: 'Target Device', width: 12 },
  ];

  actionsMenuItems: ActionMenuItem[] = [];

  constructor(private firmwareService: FirmwareService) {
    super();
  }

  async ngOnInit() {
    await this.prepareConfigs();
    this.actionsMenuItems = [
      {
        label: 'Delete',
        icon: 'fas fa-trash',
        visible: () => !!this.permissions[this.USER_ROLE.ADMIN],
        command: ({ rowData }) => this.openDeleteConfirmation(rowData),
      },
    ];
    this.getFirmwares();
    this.getSupportedDevice();
  }

  getFirmwares() {
    this.isLoading = true;
    this.firmwareService
      .getFirmwares(this.breadcrumbConfig?.projectId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (rs) => {
          this.firmwares = (rs as any[]) || [];
        },
        error: (err: any) => {
          this.showErrorMessage(err);
        },
      });
  }

  getSupportedDevice() {
    this.isLoading = true;
    this.firmwareService
      .getSupportedDevices()
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (rs) => {
          this.supportedDevices = ((rs as any[]) || []).map((device: any) => ({
            ...device,
            value: device.name,
            label: device.name,
          }));
        },
        error: (err: any) => {
          this.showErrorMessage(err);
        },
      });
  }

  getDeviceFirmware() {
    this.isLoading = true;
    this.firmwareService
      .getFirmwares(this.breadcrumbConfig?.projectId, this.device.id)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (rs) => {
          const currentDevice = ((rs?.assigned_devices as any[]) || [])?.find((p) => p.device_id === this.device.id) || null;
          const result = {
            ...rs,
            status: currentDevice?.current_status,
            current_version: currentDevice?.current_version,
          };
          this.deviceFirmware = result;
        },
        error: (err: any) => {
          this.showErrorMessage(err);
        },
      });
  }

  openUploadFirmwareForm() {
    const dialog = this.dialogSrv.open(FirmwareUploadFormComponent, {
      data: {
        supportedDevices: this.supportedDevices,
      },
      header: 'Upload Firmware',
      width: '800px',
      height: 'min-content',
      closeOnEscape: true,
      modal: true,
    });
    dialog.onClose.subscribe((rs) => {
      if (!!rs) {
        this.getFirmwares();
      }
    });
  }

  openFirmwarePushForm() {
    const dialog = this.dialogSrv.open(FirmwarePushFormComponent, {
      data: {
        firmware: this.deviceFirmware,
        devices: [{ device_id: this.device.id }],
      },
      header: 'Push Firmware',
      width: '800px',
      height: 'min-content',
      closeOnEscape: true,
      modal: true,
    });
    dialog.onClose.subscribe((rs) => {
      if (!!rs) {
        this.getFirmwares();
        this.getDeviceFirmware();
      }
    });
  }

  openAssignFirmwareForm() {
    const dialog = this.dialogSrv.open(AssignFirmwareFormComponent, {
      data: {
        firmwares: this.firmwares,
        device: this.device,
      },
      header: 'Assign Firmware',
      width: '800px',
      height: 'min-content',
      closeOnEscape: true,
      modal: true,
    });
    dialog.onClose.subscribe((rs) => {
      if (!!rs) {
        this.getFirmwares();
        this.getDeviceFirmware();
      }
    });
  }

  openDeleteConfirmation(firmware: any) {
    this.confirm({
      action: DELETE_LABEL,
      objectName: 'Firmware',
      object: firmware,
      objectFieldNameFunction: (object) => `Firmware ID ${object.id} ${!!object.name ? ` : ${object.name}` : ''}`,
      prepareRequest: () => {
        this.isLoading = true;
      },
      acceptRequest: this.firmwareService.deleteFirmware(firmware.id, this.breadcrumbConfig?.organizationId),
      next: () => {
        this.showSuccessMessage(`Deleted firmware ${firmware.id} successfully`);
        this.getFirmwares();
        this.getDeviceFirmware();
        this.isLoading = false;
      },
      error: (err) => {
        this.showErrorMessage(err);
        this.isLoading = false;
      },
    });
  }
}
